import React from 'react';
import { Award, TrendingUp, Sparkles, CheckCircle2, Zap, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
const DAYS_LEFT = 17;
interface BonusQualificationProps {
  currentPoints: number;
  goalPoints: number;
  unitsClosed: number;
  onClick?: () => void;
}
export function BonusQualification({
  currentPoints,
  goalPoints,
  unitsClosed,
  onClick
}: BonusQualificationProps) {
  const remaining = Math.max(0, goalPoints - currentPoints);
  const percent = Math.min(100, Math.round(currentPoints / goalPoints * 1000) / 10);
  const qualified = currentPoints >= goalPoints;
  const dailyPaceNeeded = Math.ceil(remaining / Math.max(1, DAYS_LEFT));
  const onTrack = dailyPaceNeeded <= 420;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.05
  }} className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition-all' : ''}`} onClick={onClick} data-id="element-139" data-mp="1bpj4go5o2gw3">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0" data-id="element-140" data-mp="3r4h8ne0dnr25">
        {/* Left: the main qualification status */}
        <div className="p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-gray-100" data-id="element-141" data-mp="3ce1jxp8r60z8">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap" data-id="element-142" data-mp="3hde5m6nw1208">
            <div className="flex items-center gap-2.5 min-w-0" data-id="element-143" data-mp="33emvswg262hw">
              <div className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center shrink-0" data-id="element-144" data-mp="38f25ne14srv4">
                <Award className="w-5 h-5 text-yellow-600" data-id="element-145" data-mp="2ti1ou5khahbf" />
              </div>
              <div className="min-w-0" data-id="element-146" data-mp="2yg37ipdzsemd">
                <h2 className="text-base sm:text-lg font-extrabold text-[#0a1e5e] leading-tight" data-id="element-147" data-mp="2yh4iktv0d143">
                  Mortgage Depot Scorecard
                </h2>
                <p className="text-xs text-gray-500 leading-tight mt-0.5" data-id="element-148" data-mp="woq301fxs8qu">
                  May 2025 · Monthly Scorecard Goal
                </p>
              </div>
            </div>

            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shrink-0 ${qualified ? 'bg-green-100 text-green-700' : onTrack ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-149" data-mp="q8pxqzfvxle1">
              {qualified ? <>
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-150" data-mp="1dx96fg2uk06n" /> Qualified
                </> : onTrack ? <>
                  <Zap className="w-3.5 h-3.5" data-id="element-151" data-mp="191aly3kn32bi" /> On Track
                </> : <>
                  <TrendingUp className="w-3.5 h-3.5" data-id="element-152" data-mp="106vyvetvanhu" /> Push Needed
                </>}
            </span>
          </div>

          {/* Big point comparison */}
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-5 items-end mb-5" data-id="element-153" data-mp="1wq3c1jekyfy7">
            <div className="min-w-0" data-id="element-154" data-mp="1rwsde5jx95hd">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-155" data-mp="3al3axgkyn6wp">
                You Have
              </p>
              <div className="flex items-baseline gap-1.5 flex-wrap" data-id="element-156" data-mp="3fkpo5q0sgn5i">
                <span className={`text-4xl sm:text-5xl font-extrabold leading-none tracking-tight ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-157" data-mp="3jl06811uv40q">
                  {currentPoints.toLocaleString()}
                </span>
                <span className="text-sm font-bold text-gray-500" data-id="element-158" data-mp="3thl9ohy8872g">PTS</span>
              </div>
            </div>
            <div className="hidden sm:flex items-end justify-center pb-2 text-gray-300 text-2xl font-bold" data-id="element-159" data-mp="2b1ruitz3gun">
              /
            </div>
            <div className="min-w-0 sm:text-right" data-id="element-160" data-mp="b4n5fyueis2h">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-161" data-mp="b7j27juijapc">
                You Need
              </p>
              <div className="flex sm:justify-end items-baseline gap-1.5 flex-wrap" data-id="element-162" data-mp="l32za9ktcffo">
                <span className="text-4xl sm:text-5xl font-extrabold text-gray-400 leading-none tracking-tight" data-id="element-163" data-mp="7gneu9j24cua">
                  {goalPoints.toLocaleString()}
                </span>
                <span className="text-sm font-bold text-gray-400" data-id="element-164" data-mp="h6ytaq0da4ff">PTS</span>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4" data-id="element-165" data-mp="c8dtgrp11mri">
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-166" data-mp="3knp5jmmjldpk">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${percent}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} className={`h-3 rounded-full ${qualified ? 'bg-green-500' : 'bg-blue-600'}`} data-id="element-167" data-mp="3kprus4fvf2t8" />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] font-semibold" data-id="element-168" data-mp="14wt657korszo">
              <span className={qualified ? 'text-green-600' : 'text-blue-600'} data-id="element-169" data-mp="14yvxpw1hlu6i">
                {percent}% complete
              </span>
              <span className="text-gray-500" data-id="element-170" data-mp="1hff0th4f0qp7">
                {qualified ? '🎉 Bonus unlocked' : `${remaining.toLocaleString()} pts to go`}
              </span>
            </div>
          </div>

          {/* Daily pace pill */}
          {!qualified && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2.5" data-id="element-171" data-mp="1v6a2a8cl45ir">
              <Zap className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" data-id="element-172" data-mp="205wjesvja5wu" />
              <div className="min-w-0 text-sm" data-id="element-173" data-mp="1mehwwc2f3ml1">
                <span className="font-bold text-[#0a1e5e]" data-id="element-174" data-mp="1mdqclxe9tba2">
                  {dailyPaceNeeded} pts/day
                </span>
                <span className="text-gray-700" data-id="element-175" data-mp="1qdqsznkxjwjr">
                  {' '}
                  for the next {DAYS_LEFT} days will get you across the line.
                </span>
              </div>
            </div>}
        </div>

        {/* Right: What you unlock — entire section locked until scorecard goal is met */}
        {qualified ? <div className="bg-gradient-to-br from-yellow-50 to-amber-50/40 p-5 sm:p-6 flex flex-col" data-id="element-176" data-mp="2orkp86n2w2n2">
            <div className="flex items-center gap-2 mb-3" data-id="element-177" data-mp="2jqdhg7kxr3ge">
              <Sparkles className="w-4 h-4 text-yellow-600 shrink-0" data-id="element-178" data-mp="2esbx7jbrwy3c" />
              <h3 className="text-xs font-bold text-yellow-800 uppercase tracking-wider" data-id="element-179" data-mp="2es3ng0ay68wd">
                What This Unlocks
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-4 leading-snug" data-id="element-180" data-mp="252a76lzv15ls">
              You hit your monthly goal — you've unlocked{' '}
              <strong className="text-[#0a1e5e]" data-id="element-181" data-mp="2116ug8d3zpos">Unit Bonus eligibility</strong>{' '}
              on top of your base comp.
            </p>

            <ul className="space-y-2 text-sm" data-id="element-182" data-mp="1r2hnvbqnxrnu">
              <BonusTier label="4+ Units" threshold={4} bonus={5} unitsClosed={unitsClosed} goalMet={qualified} data-id="element-183" data-mp="1r2991eazcca0" />
              <BonusTier label="6+ Units" threshold={6} bonus={20} unitsClosed={unitsClosed} goalMet={qualified} highlight data-id="element-184" data-mp="1bzhkwemussq3" />
              <BonusTier label="10+ Units" threshold={10} bonus={30} unitsClosed={unitsClosed} goalMet={qualified} data-id="element-185" data-mp="1qx04z3ol6hr7" />
            </ul>

            <p className="text-[11px] text-gray-500 leading-snug mt-4 pt-3 border-t border-yellow-200/60" data-id="element-186" data-mp="y7jkcs77k55j">
              Miss the monthly goal →{' '}
              <strong className="text-red-600" data-id="element-187" data-mp="135co7el1gn2n">no Unit Bonus</strong> for the
              month, even if units are met.
            </p>
          </div> : <div className="relative bg-gray-50 p-5 sm:p-6 flex flex-col" data-id="element-188" data-mp="ahyx3x40ulqg">
            {/* Greyed-out preview behind the lock */}
            <div aria-hidden="true" className="flex flex-col opacity-75 blur-[0.3px] select-none pointer-events-none" data-id="element-189" data-mp="ffqpes8kw9v6">
              <div className="flex items-center gap-2 mb-3" data-id="element-190" data-mp="1pnrk7j4er3qj">
                <Sparkles className="w-4 h-4 text-gray-400 shrink-0" data-id="element-191" data-mp="23mhfct7o2tc9" />
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-192" data-mp="23keibp83jzy1">
                  What This Unlocks
                </h3>
              </div>
              <ul className="space-y-2 text-sm" data-id="element-193" data-mp="2cm4awydpt4xe">
                {[{
              label: '4+ Units',
              bonus: 5
            }, {
              label: '6+ Units',
              bonus: 20
            }, {
              label: '10+ Units',
              bonus: 30
            }].map(t => <li key={t.label} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 bg-gray-100 border border-gray-200" data-id="element-194" data-mp="2w929g1fj1jji">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-195" data-mp="3avra46lim2rh">
                      {t.label}
                    </span>
                    <span className="text-base font-extrabold text-gray-400" data-id="element-196" data-mp="30ymjnukmzl4v">
                      +{t.bonus} BPS
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Lock overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-197" data-mp="2r81l274yqgly">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mb-3" data-id="element-198" data-mp="2i96hz3x7tmmv">
                <Lock className="w-6 h-6 text-gray-500" data-id="element-199" data-mp="2n8186qxiidh0" />
              </div>
              <p className="text-sm font-bold text-gray-700 leading-snug max-w-[220px]" data-id="element-200" data-mp="2n5wuxidq4242">
                Reach your Scorecard Goal to Unlock Bonus Eligibility
              </p>
              <p className="text-xs text-gray-500 mt-2 font-medium" data-id="element-201" data-mp="2d9ldkdza0m2r">
                {remaining.toLocaleString()} pts to go
              </p>
            </div>
          </div>}
      </div>
    </motion.div>;
}
function BonusTier({
  label,
  threshold,
  bonus,
  unitsClosed,
  goalMet,
  highlight
}: {
  label: string;
  threshold: number;
  bonus: number;
  unitsClosed: number;
  goalMet: boolean;
  highlight?: boolean;
}) {
  const unitsMet = unitsClosed >= threshold;
  const unlocked = goalMet && unitsMet;
  const unitsToGo = threshold - unitsClosed;
  return <li className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors ${unlocked ? highlight ? 'bg-white border border-yellow-200 shadow-sm' : '' : 'bg-gray-50 border border-gray-100'}`} data-id="element-202" data-mp="joum9ub4mj0x">
      <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${unlocked ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-203" data-mp="9wyka85x68rf">
        {!unlocked && <Lock className="w-3 h-3 shrink-0" data-id="element-204" data-mp="12g436on88a2j" />}
        {label}
      </span>
      <div className="flex items-center gap-2.5 shrink-0" data-id="element-205" data-mp="2n4y4ge44jyuq">
        {!unlocked && <span className="text-[10px] font-semibold text-gray-400" data-id="element-206" data-mp="2i6ekgj93cx50">
            {!goalMet ? 'Hit point goal' : `${unitsToGo} more unit${unitsToGo === 1 ? '' : 's'}`}
          </span>}
        <div className="flex items-baseline gap-1" data-id="element-207" data-mp="2rvy0zi9c0ewp">
          <span className={`text-base font-extrabold ${unlocked ? 'text-green-600' : 'text-gray-400'}`} data-id="element-208" data-mp="2ryar4ip8tvku">
            +{bonus}
          </span>
          <span className="text-[10px] font-bold text-gray-500 ml-0.5" data-id="element-209" data-mp="1z8u3dyckj2uy">
            BPS
          </span>
        </div>
      </div>
    </li>;
}